import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { AssetActionModal } from './AssetActionModal';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import AppIcon, { AppIconName } from './AppIcon';

interface AssetTypeConfig {
  key: 'nps' | 'ppf' | 'epf' | 'gold' | 'lic' | 'real_estate' | 'fixed_deposit' | 'bank_account';

  name: string;
  ctaLabel: string;
  description: string;
  icon: AppIconName;
  accentGradient: string;
}

type AssetKey = AssetTypeConfig['key'];

interface AssetEntry {
  id: number;
  entry_date: string;
  balance: number;
  employee_contribution: number;
  employer_contribution: number;
  pension_contribution: number;
  interest_amount: number;
  interest_rate: number | null;
  monthly_contribution: number | null;
  source: string;
  notes: string;
  metadata: Record<string, any>;
}

interface AssetComponent {
  name: string;
  current_value: number;
  contribution: number;
  interest: number;
  status: string;
}

interface AssetSummary {
  total_value: number;
  investment: number;
  gain_loss: number;
  return_percent: number;
  xirr: number | null;
  schemes: any[];
  components: AssetComponent[];
  entries: AssetEntry[];
  has_asset: boolean;
  interest_rate: number | null;
  monthly_contribution: number | null;
  projected_value: number | null;
  projected_interest: number | null;
  metadata: Record<string, any>;
}

type AssetsData = Record<AssetKey, AssetSummary>;

const emptyAssetSummary: AssetSummary = {
  total_value: 0,
  investment: 0,
  gain_loss: 0,
  return_percent: 0,
  xirr: null,
  schemes: [],
  components: [],
  entries: [],
  has_asset: false,
  interest_rate: null,
  monthly_contribution: null,
  projected_value: null,
  projected_interest: null,
  metadata: {}
};

const emptyAssetsData: AssetsData = {
  nps: { ...emptyAssetSummary },
  ppf: { ...emptyAssetSummary },
  epf: { ...emptyAssetSummary },
  gold: { ...emptyAssetSummary },
  lic: { ...emptyAssetSummary },
  real_estate: { ...emptyAssetSummary },
  fixed_deposit: { ...emptyAssetSummary },
  bank_account: { ...emptyAssetSummary }
};


const assetTypes: AssetTypeConfig[] = [
  {
    key: 'nps',
    name: 'National Pension System',
    ctaLabel: 'Import Statement',
    description: 'Government-backed retirement planning with tax benefits under Section 80CCD.',
    icon: 'bank',
    accentGradient: 'linear-gradient(90deg, var(--pd-lavender), var(--pd-blue))'
  },
  {
    key: 'ppf',
    name: 'Public Provident Fund',
    ctaLabel: 'Update Balance',
    description: 'Long-term secure savings with guaranteed tax-free returns under Section 80C.',
    icon: 'savings',
    accentGradient: 'linear-gradient(90deg, #198754, #40c786)'
  },
  {
    key: 'epf',
    name: 'Employee Provident Fund',
    ctaLabel: 'Update EPF',
    description: 'Salaried employee retirement benefits co-contributed by your employer.',
    icon: 'briefcase',
    accentGradient: 'linear-gradient(90deg, var(--pd-blue), var(--pd-blue-light))'
  },
  {
    key: 'gold',
    name: 'Gold Holdings',
    ctaLabel: 'Add Gold',
    description: 'Digital and in-hand gold tracked by grams, purchase price, and current market value.',
    icon: 'gold',
    accentGradient: 'linear-gradient(90deg, var(--pd-pink), var(--pd-pink-light))'
  },
  {
    key: 'lic',
    name: 'LIC / Insurance',
    ctaLabel: 'Add Policy',
    description: 'Life insurance policies, endowment plans, and term insurance tracking.',
    icon: 'shield',
    accentGradient: 'linear-gradient(90deg, #ffc107, #ff9800)'
  },
  {
    key: 'real_estate',
    name: 'Real Estate',
    ctaLabel: 'Add Property',
    description: 'Track residential/commercial property values, equity, loans, and rental incomes.',
    icon: 'building',
    accentGradient: 'linear-gradient(90deg, #84fab0 0%, #8fd3f4 100%)'
  },
  {
    key: 'fixed_deposit',
    name: 'Fixed Deposits',
    ctaLabel: 'Add FD',
    description: 'Track bank FDs with principal, interest rate, maturity date, and auto-calculated current value.',
    icon: 'deposit',
    accentGradient: 'linear-gradient(90deg, #f7971e 0%, #ffd200 100%)'
  },
  {
    key: 'bank_account',
    name: 'Bank Accounts',
    ctaLabel: 'Add Account',
    description: 'Track savings, salary, and current account balances as liquid net worth.',
    icon: 'assets',
    accentGradient: 'linear-gradient(90deg, #00b4db 0%, #0083b0 100%)'
  }
];

const trackedItemLabels: Record<AssetKey, string> = {
  nps: 'Accounts',
  ppf: 'Accounts',
  epf: 'Members',
  gold: 'Holdings',
  lic: 'Policies',
  real_estate: 'Properties',
  fixed_deposit: 'Deposits',
  bank_account: 'Accounts',
};


const assetDetailPlaceholders: Record<AssetKey, { eyebrow: string; title: string; summaryLabel: string; emptyText: string; columns: string[]; rows: string[][] }> = {
  nps: {
    eyebrow: 'NPS account details',
    title: 'National Pension System breakdown',
    summaryLabel: 'Accounts tracked',
    emptyText: 'Import family NPS statements to compare account-wise current value, investment, gain/loss, and XIRR.',
    columns: ['Account', 'Current value', 'Investment', 'Gain/Loss', 'XIRR'],
    rows: [['NPS account', 'Not imported', 'Not imported', 'Not imported', 'Not imported']]
  },
  ppf: {
    eyebrow: 'PPF account details',
    title: 'Public Provident Fund breakdown',
    summaryLabel: 'Accounts held',
    emptyText: 'Add family PPF balances to track contributions, interest rate, and maturity progress.',
    columns: ['Account', 'Current balance', 'Monthly contribution', 'Interest rate', 'Projected FY-end'],
    rows: [['Primary PPF', 'Not added', 'Not added', 'Not added', 'Not added']]
  },
  epf: {
    eyebrow: 'EPF passbook details',
    title: 'Employee Provident Fund breakdown',
    summaryLabel: 'Members tracked',
    emptyText: 'Import an EPF passbook or add family members manually to view employee, employer, and pension shares.',
    columns: ['Member', 'Current value', 'Employee share', 'Employer / pension', 'Interest rate'],
    rows: [['Employee share', 'Not imported', 'Not imported', 'Not imported', 'Pending'], ['Employer share', 'Not imported', 'Not imported', 'Not imported', 'Pending']]
  },
  gold: {
    eyebrow: 'Gold holding details',
    title: 'Gold holdings breakdown',
    summaryLabel: 'Spot rate',
    emptyText: 'Add digital or in-hand gold to compare grams held, average buy price, market value, and profit.',
    columns: ['Holding', 'Current value', 'Grams', 'Buy / current price', 'Gain/Loss'],
    rows: [['Gold holdings', 'Not added', 'Not added', 'Not added', 'Not added']]
  },
  lic: {
    eyebrow: 'LIC / Insurance details',
    title: 'Insurance policies breakdown',
    summaryLabel: 'Policies held',
    emptyText: 'Add your insurance policies to track premiums, sum assured, and maturity/due dates.',
    columns: ['Policy Name', 'Current Value', 'Premium', 'Sum Assured', 'Maturity / Due'],
    rows: [['No policies', 'Not added', 'Not added', 'Not added', 'Not added']]
  },
  real_estate: {
    eyebrow: 'Real Estate details',
    title: 'Property holdings breakdown',
    summaryLabel: 'Net Equity',
    emptyText: 'Add your property details to track property cost, current value, outstanding loan, and rental income.',
    columns: ['Property Name', 'Net Equity', 'Property Cost', 'Loan Outstanding', 'Monthly Rent'],
    rows: [['No properties', 'Not added', 'Not added', 'Not added', 'Not added']]
  },
  fixed_deposit: {
    eyebrow: 'Fixed Deposit details',
    title: 'Fixed Deposits breakdown',
    summaryLabel: 'FDs held',
    emptyText: 'Add your FDs to track principal, interest rate, maturity value, and accrued interest.',
    columns: ['FD Name / Bank', 'Current Value', 'Principal', 'Interest Rate', 'Maturity Date'],
    rows: [['No FDs', 'Not added', 'Not added', 'Not added', 'Not added']]
  },
  bank_account: {
    eyebrow: 'Bank account details',
    title: 'Bank Accounts breakdown',
    summaryLabel: 'Accounts held',
    emptyText: 'Add bank account balances to include liquid cash in your net worth.',
    columns: ['Account', 'Balance', 'Bank', 'Type', 'Last Updated'],
    rows: [['No accounts', 'Not added', 'Not added', 'Not added', 'Not added']]
  }
};


const Assets: React.FC = () => {
  const { token } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<AssetTypeConfig | null>(null);
  const [selectedDetailKey, setSelectedDetailKey] = useState<AssetKey>('nps');
  const [policyToEdit, setPolicyToEdit] = useState<any | null>(null);
  const [propertyToEdit, setPropertyToEdit] = useState<any | null>(null);
  const [fdToEdit, setFdToEdit] = useState<any | null>(null);
  const [bankAccountToEdit, setBankAccountToEdit] = useState<any | null>(null);
  const [ppfAccountToEdit, setPpfAccountToEdit] = useState<any | null>(null);
  const [epfMemberToEdit, setEpfMemberToEdit] = useState<any | null>(null);


  const [portfolioValue, setPortfolioValue] = useState<number>(0);
  const [assetsData, setAssetsData] = useState<AssetsData>(emptyAssetsData);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const fetchAssets = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Token ${token}` };
    setAssetsLoading(true);
    setPortfolioLoading(true);
    setLoadError('');

    const assetsRequest = axios.get(`${API_BASE_URL}/assets/`, { headers })
      .then((assetsResponse) => {
        setAssetsData({
          nps: { ...emptyAssetSummary, ...(assetsResponse.data.nps || {}) },
          ppf: { ...emptyAssetSummary, ...(assetsResponse.data.ppf || {}) },
          epf: { ...emptyAssetSummary, ...(assetsResponse.data.epf || {}) },
          gold: { ...emptyAssetSummary, ...(assetsResponse.data.gold || {}) },
          lic: { ...emptyAssetSummary, ...(assetsResponse.data.lic || {}) },
          real_estate: { ...emptyAssetSummary, ...(assetsResponse.data.real_estate || {}) },
          fixed_deposit: { ...emptyAssetSummary, ...(assetsResponse.data.fixed_deposit || {}) },
          bank_account: { ...emptyAssetSummary, ...(assetsResponse.data.bank_account || {}) }
        });
      })
      .catch((err) => {
        console.error('Failed to fetch assets summary:', err);
        setLoadError('Some wealth data could not be loaded.');
      })
      .finally(() => setAssetsLoading(false));

    const portfolioRequest = Promise.all([
      axios.get(`${API_BASE_URL}/portfolio/mutual-funds/`, { headers }),
      axios.get(`${API_BASE_URL}/portfolio/stocks/`, { headers }),
    ])
      .then(([mutualFundsResponse, stocksResponse]) => {
        const mutualFundValue = (mutualFundsResponse.data || []).reduce(
          (sum: number, holding: any) => sum + Number(holding.current_value || 0),
          0,
        );
        const stockValue = (stocksResponse.data || []).reduce(
          (sum: number, holding: any) => (
            holding.current_value === null || holding.current_value === undefined
              ? sum
              : sum + Number(holding.current_value)
          ),
          0,
        );
        setPortfolioValue(mutualFundValue + stockValue);
      })
      .catch(async (holdingsError) => {
        console.warn(
          'Failed to load portfolio holdings in assets page; using summary fallback:',
          holdingsError,
        );
        const portfolioResponse = await axios.get(
          `${API_BASE_URL}/portfolio/summary/`,
          { headers },
        );
        setPortfolioValue(Number(portfolioResponse.data.current_value || 0));
      })
      .catch((summaryError) => {
        console.error('Failed to fetch portfolio value in assets page:', summaryError);
        setLoadError('Some wealth data could not be loaded.');
      })
      .finally(() => setPortfolioLoading(false));

    await Promise.allSettled([assetsRequest, portfolioRequest]);
  }, [token]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleOpenModal = (asset: AssetTypeConfig) => {
    setPolicyToEdit(null);
    setPropertyToEdit(null);
    setFdToEdit(null);
    setBankAccountToEdit(null);
    setPpfAccountToEdit(null);
    setEpfMemberToEdit(null);
    setSelectedAsset(asset);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAsset(null);
    setPolicyToEdit(null);
    setPropertyToEdit(null);
    setFdToEdit(null);
    setBankAccountToEdit(null);
    setPpfAccountToEdit(null);
    setEpfMemberToEdit(null);
  };


  const handleEditPolicy = (policy: any) => {
    setPropertyToEdit(null);
    setFdToEdit(null);
    setBankAccountToEdit(null);
    setPpfAccountToEdit(null);
    setEpfMemberToEdit(null);
    setPolicyToEdit(policy);
    setSelectedAsset(assetTypes.find((a) => a.key === 'lic') || null);
    setShowModal(true);
  };

  const handleEditProperty = (property: any) => {
    setPolicyToEdit(null);
    setFdToEdit(null);
    setBankAccountToEdit(null);
    setPpfAccountToEdit(null);
    setEpfMemberToEdit(null);
    setPropertyToEdit(property);
    setSelectedAsset(assetTypes.find((a) => a.key === 'real_estate') || null);
    setShowModal(true);
  };

  const handleEditFD = (fd: any) => {
    setPolicyToEdit(null);
    setPropertyToEdit(null);
    setBankAccountToEdit(null);
    setPpfAccountToEdit(null);
    setEpfMemberToEdit(null);
    setFdToEdit(fd);
    setSelectedAsset(assetTypes.find((a) => a.key === 'fixed_deposit') || null);
    setShowModal(true);
  };

  const handleEditBankAccount = (account: any) => {
    setPolicyToEdit(null);
    setPropertyToEdit(null);
    setFdToEdit(null);
    setPpfAccountToEdit(null);
    setEpfMemberToEdit(null);
    setBankAccountToEdit(account);
    setSelectedAsset(assetTypes.find((a) => a.key === 'bank_account') || null);
    setShowModal(true);
  };

  const handleEditPPFAccount = (account: any) => {
    setPolicyToEdit(null);
    setPropertyToEdit(null);
    setFdToEdit(null);
    setBankAccountToEdit(null);
    setEpfMemberToEdit(null);
    setPpfAccountToEdit(account);
    setSelectedAsset(assetTypes.find((a) => a.key === 'ppf') || null);
    setShowModal(true);
  };

  const handleEditEPFMember = (member: any) => {
    setPolicyToEdit(null);
    setPropertyToEdit(null);
    setFdToEdit(null);
    setBankAccountToEdit(null);
    setPpfAccountToEdit(null);
    setEpfMemberToEdit(member);
    setSelectedAsset(assetTypes.find((a) => a.key === 'epf') || null);
    setShowModal(true);
  };


  const handleModalSubmit = (data: any) => {
    if (data && data.status === 'success' && data.asset_type && data.asset_type in assetsData) {
      const assetKey = data.asset_type as AssetKey;
      setAssetsData((prev) => ({
        ...prev,
        [assetKey]: {
          ...prev[assetKey],
          ...data,
          schemes: data.schemes || [],
          components: data.components || [],
          entries: data.entries || [],
          has_asset: true
        }
      }));
      fetchAssets();
    } else {
      fetchAssets();
    }
  };

  const handleDeletePolicy = async (policyId: string) => {
    if (!token) return;
    if (!window.confirm("Are you sure you want to delete this policy?")) return;
    try {
      const headers = { Authorization: `Token ${token}` };
      const response = await axios.post(`${API_BASE_URL}/assets/lic/manual/`, {
        action: 'delete',
        id: policyId
      }, { headers });
      
      setAssetsData((prev) => ({
        ...prev,
        lic: {
          ...prev.lic,
          ...response.data,
          components: response.data.components || [],
          has_asset: response.data.total_value > 0
        }
      }));
      fetchAssets();
    } catch (err) {
      console.error("Failed to delete policy:", err);
      alert("Failed to delete policy.");
    }
  };

  const handleDeleteProperty = async (propertyId: string) => {
    if (!token) return;
    if (!window.confirm("Are you sure you want to delete this property?")) return;
    try {
      const headers = { Authorization: `Token ${token}` };
      const response = await axios.post(`${API_BASE_URL}/assets/real-estate/manual/`, {
        action: 'delete',
        id: propertyId
      }, { headers });
      
      setAssetsData((prev) => ({
        ...prev,
        real_estate: {
          ...prev.real_estate,
          ...response.data,
          components: response.data.components || [],
          has_asset: response.data.total_value > 0
        }
      }));
      fetchAssets();
    } catch (err) {
      console.error("Failed to delete property:", err);
      alert("Failed to delete property.");
    }
  };

  const handleDeleteFD = async (fdId: string) => {
    if (!token) return;
    if (!window.confirm("Are you sure you want to delete this Fixed Deposit?")) return;
    try {
      const headers = { Authorization: `Token ${token}` };
      const response = await axios.post(`${API_BASE_URL}/assets/fixed-deposit/manual/`, {
        action: 'delete',
        id: fdId
      }, { headers });
      setAssetsData((prev) => ({
        ...prev,
        fixed_deposit: {
          ...prev.fixed_deposit,
          ...response.data,
          components: response.data.components || [],
          has_asset: response.data.total_value > 0
        }
      }));
      fetchAssets();
    } catch (err) {
      console.error("Failed to delete FD:", err);
      alert("Failed to delete Fixed Deposit.");
    }
  };

  const handleDeleteBankAccount = async (accountId: string) => {
    if (!token) return;
    if (!window.confirm("Are you sure you want to delete this bank account?")) return;
    try {
      const headers = { Authorization: `Token ${token}` };
      const response = await axios.post(`${API_BASE_URL}/assets/bank-account/manual/`, {
        action: 'delete',
        id: accountId
      }, { headers });
      setAssetsData((prev) => ({
        ...prev,
        bank_account: {
          ...prev.bank_account,
          ...response.data,
          components: response.data.components || [],
          has_asset: response.data.total_value > 0
        }
      }));
      fetchAssets();
    } catch (err) {
      console.error("Failed to delete bank account:", err);
      alert("Failed to delete bank account.");
    }
  };

  const handleDeletePPF = async () => {
    if (!token) return;
    if (!window.confirm("Are you sure you want to delete your PPF account?")) return;
    try {
      const headers = { Authorization: `Token ${token}` };
      await axios.post(`${API_BASE_URL}/assets/ppf/manual/`, {
        action: 'delete'
      }, { headers });
      setAssetsData((prev) => ({
        ...prev,
        ppf: {
          ...prev.ppf,
          total_value: 0,
          investment: 0,
          gain_loss: 0,
          has_asset: false
        }
      }));
      fetchAssets();
    } catch (err) {
      console.error("Failed to delete PPF:", err);
      alert("Failed to delete PPF.");
    }
  };

  const handleDeletePPFAccount = async (accountId: string) => {
    if (!token) return;
    if (!window.confirm("Are you sure you want to delete this PPF account?")) return;
    try {
      const headers = { Authorization: `Token ${token}` };
      const response = await axios.post(`${API_BASE_URL}/assets/ppf/manual/`, {
        action: 'delete',
        id: accountId
      }, { headers });
      setAssetsData((prev) => ({
        ...prev,
        ppf: {
          ...prev.ppf,
          ...response.data,
          components: response.data.components || [],
          entries: response.data.entries || [],
          has_asset: response.data.total_value > 0
        }
      }));
      fetchAssets();
    } catch (err) {
      console.error("Failed to delete PPF account:", err);
      alert("Failed to delete PPF account.");
    }
  };

  const handleDeleteEPF = async () => {
    if (!token) return;
    if (!window.confirm("Are you sure you want to delete your EPF account?")) return;
    try {
      const headers = { Authorization: `Token ${token}` };
      await axios.post(`${API_BASE_URL}/assets/epf/manual/`, {
        action: 'delete'
      }, { headers });
      setAssetsData((prev) => ({
        ...prev,
        epf: {
          ...prev.epf,
          total_value: 0,
          investment: 0,
          gain_loss: 0,
          has_asset: false
        }
      }));
      fetchAssets();
    } catch (err) {
      console.error("Failed to delete EPF:", err);
      alert("Failed to delete EPF.");
    }
  };

  const handleDeleteEPFMember = async (memberId: string) => {
    if (!token) return;
    if (!window.confirm("Are you sure you want to delete this EPF member?")) return;
    try {
      const headers = { Authorization: `Token ${token}` };
      const response = await axios.post(`${API_BASE_URL}/assets/epf/manual/`, {
        action: 'delete',
        id: memberId
      }, { headers });
      setAssetsData((prev) => ({
        ...prev,
        epf: {
          ...prev.epf,
          ...response.data,
          components: response.data.components || [],
          entries: response.data.entries || [],
          has_asset: response.data.total_value > 0
        }
      }));
      fetchAssets();
    } catch (err) {
      console.error("Failed to delete EPF member:", err);
      alert("Failed to delete EPF member.");
    }
  };

  const handleDeleteGold = async () => {
    if (!token) return;
    if (!window.confirm("Are you sure you want to delete your Gold holdings?")) return;
    try {
      const headers = { Authorization: `Token ${token}` };
      await axios.post(`${API_BASE_URL}/assets/gold/manual/`, {
        action: 'delete'
      }, { headers });
      setAssetsData((prev) => ({
        ...prev,
        gold: {
          ...prev.gold,
          total_value: 0,
          investment: 0,
          gain_loss: 0,
          has_asset: false
        }
      }));
      fetchAssets();
    } catch (err) {
      console.error("Failed to delete Gold:", err);
      alert("Failed to delete Gold.");
    }
  };

  const handleDeleteNPS = async () => {
    if (!token) return;
    if (!window.confirm("Are you sure you want to delete your NPS statement details?")) return;
    try {
      const headers = { Authorization: `Token ${token}` };
      await axios.post(`${API_BASE_URL}/assets/import/nps/`, {
        action: 'delete'
      }, { headers });
      setAssetsData((prev) => ({
        ...prev,
        nps: {
          ...prev.nps,
          total_value: 0,
          investment: 0,
          gain_loss: 0,
          has_asset: false
        }
      }));
      fetchAssets();
    } catch (err) {
      console.error("Failed to delete NPS:", err);
      alert("Failed to delete NPS.");
    }
  };

  const handleDeleteNPSAccount = async (accountId: string) => {
    if (!token) return;
    if (!window.confirm("Are you sure you want to delete this NPS account?")) return;
    try {
      const headers = { Authorization: `Token ${token}` };
      const response = await axios.post(`${API_BASE_URL}/assets/import/nps/`, {
        action: 'delete',
        id: accountId
      }, { headers });
      setAssetsData((prev) => ({
        ...prev,
        nps: {
          ...prev.nps,
          ...response.data,
          schemes: response.data.schemes || [],
          components: response.data.components || [],
          has_asset: response.data.total_value > 0
        }
      }));
      fetchAssets();
    } catch (err) {
      console.error("Failed to delete NPS account:", err);
      alert("Failed to delete NPS account.");
    }
  };



  const formatINR = (val: number) =>
    Number(val || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

  const formatPercent = (val: number | null | undefined) =>
    val !== null && val !== undefined ? `${Number(val).toFixed(2)}%` : 'N/A';

  const formatNavDate = (value: string | null | undefined) => {
    if (!value) return 'Statement NAV';
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return `NAV ${value}`;
    return `NAV ${parsed.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })}`;
  };

  const npsSchemes = Array.isArray(assetsData.nps?.schemes) ? assetsData.nps.schemes : [];
  const selectedDetailData = assetsData[selectedDetailKey];
  const selectedDetailConfig = assetDetailPlaceholders[selectedDetailKey];
  const selectedSummaryValue = selectedDetailKey === 'nps'
    ? (selectedDetailData.metadata?.accounts?.length ?? (selectedDetailData.has_asset ? 1 : 0))
    : selectedDetailKey === 'ppf'
      ? (selectedDetailData.metadata?.accounts?.length ?? (selectedDetailData.has_asset ? 1 : 0))
      : selectedDetailKey === 'epf'
        ? (selectedDetailData.metadata?.members?.length ?? (selectedDetailData.has_asset ? 1 : 0))
    : selectedDetailKey === 'lic'
      ? (selectedDetailData.metadata?.policies?.length ?? 0)
      : selectedDetailKey === 'real_estate'
        ? selectedDetailData.total_value
        : selectedDetailKey === 'fixed_deposit'
          ? (selectedDetailData.metadata?.fds?.length ?? 0)
          : selectedDetailKey === 'bank_account'
            ? (selectedDetailData.metadata?.accounts?.length ?? 0)
            : selectedDetailData.interest_rate;


  const renderDetailRows = () => {
    if (selectedDetailKey === 'nps' && selectedDetailData.has_asset) {
      const accounts = Array.isArray(selectedDetailData.metadata?.accounts) ? selectedDetailData.metadata.accounts : [];
      if (accounts.length > 0) {
        return accounts.map((account: any) => {
          const accountSchemes = Array.isArray(account.schemes) ? account.schemes : [];
          const navAsOf = account.nav_as_of || selectedDetailData.metadata?.nav_as_of;
          return (
            <div className="asset-scheme-table__row" key={account.id}>
              <span>
                <strong>{account.account_name || 'NPS account'}</strong>
                <small>{account.holder_name || account.imported_at || 'Family member'}</small>
                {accountSchemes.length > 0 && (
                  <span className="nps-account-navs" aria-label={`Latest NAVs for ${account.account_name || 'NPS account'}`}>
                    <span>{formatNavDate(navAsOf)}</span>
                    {accountSchemes.map((scheme: any) => (
                      <span key={`${scheme.tier || 'Tier I'}-${scheme.scheme}`}>
                        {scheme.scheme || scheme.name} ₹{Number(scheme.nav || 0).toFixed(4)}
                      </span>
                    ))}
                  </span>
                )}
              </span>
              <span>₹{formatINR(account.current_value || 0)}</span>
              <span>₹{formatINR(account.investment || 0)}</span>
              <span>
                <span className={(account.gain_loss || 0) >= 0 ? 'text-success' : 'text-danger'}>
                  ₹{formatINR(account.gain_loss || 0)}
                </span>
              </span>
              <span>
                {formatPercent(account.xirr)}
                <Button
                  variant="link"
                  className="text-primary p-0 m-0 ms-3 me-2"
                  style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
                  onClick={() => handleOpenModal(assetTypes.find(a => a.key === 'nps')!)}
                >
                  Add
                </Button>
                <Button
                  variant="link"
                  className="text-danger p-0 m-0 ms-2"
                  style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
                  onClick={() => handleDeleteNPSAccount(account.id)}
                >
                  Delete
                </Button>
              </span>
            </div>
          );
        });
      }
    }

    if (selectedDetailKey === 'nps' && npsSchemes.length > 0) {
      return npsSchemes.map((scheme: any, idx: number) => (
        <div className="asset-scheme-table__row" key={`${scheme.tier}-${scheme.scheme}`}>
          <span>
            <strong>{scheme.name || `Scheme ${scheme.scheme}`}</strong>
            <small>{scheme.tier || 'Tier I'}</small>
          </span>
          <span>₹{formatINR(Number(scheme.current_value || 0))}</span>
          <span>{Number(scheme.units || 0).toLocaleString('en-IN', { maximumFractionDigits: 4 })}</span>
          <span>₹{formatINR(Number(scheme.nav || 0))}</span>
          <span>
            {formatPercent(scheme.xirr)}
            {idx === 0 && (
              <>
                <Button
                  variant="link"
                  className="text-primary p-0 m-0 ms-3 me-2"
                  style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
                  onClick={() => handleOpenModal(assetTypes.find(a => a.key === 'nps')!)}
                >
                  Edit (Re-import)
                </Button>
                <Button
                  variant="link"
                  className="text-danger p-0 m-0 ms-2"
                  style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
                  onClick={() => handleDeleteNPS()}
                >
                  Delete
                </Button>
              </>
            )}
          </span>
        </div>
      ));
    }

    if (selectedDetailKey === 'ppf' && selectedDetailData.has_asset) {
      const accounts = Array.isArray(selectedDetailData.metadata?.accounts) ? selectedDetailData.metadata.accounts : [];
      if (accounts.length > 0) {
        return accounts.map((account: any) => (
          <div className="asset-scheme-table__row" key={account.id}>
            <span>
              <strong>{account.account_name || 'PPF account'}</strong>
              <small>{account.holder_name || account.account_reference || 'Family member'}</small>
            </span>
            <span>₹{formatINR(account.current_balance)}</span>
            <span>₹{formatINR(account.monthly_contribution || 0)}</span>
            <span>{formatPercent(account.interest_rate)}</span>
            <span>
              {account.projected_value !== null && account.projected_value !== undefined
                ? `₹${formatINR(account.projected_value)}`
                : 'N/A'}
              <Button
                variant="link"
                className="text-primary p-0 m-0 ms-3 me-2"
                style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
                onClick={() => handleEditPPFAccount(account)}
              >
                Edit
              </Button>
              <Button
                variant="link"
                className="text-danger p-0 m-0 ms-2"
                style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
                onClick={() => handleDeletePPFAccount(account.id)}
              >
                Delete
              </Button>
            </span>
          </div>
        ));
      }
      const latestEntry = selectedDetailData.entries[0];
      return (
        <div className="asset-scheme-table__row">
          <span>
            <strong>{selectedDetailData.metadata?.account_reference || 'Primary PPF'}</strong>
            <small>{latestEntry?.entry_date || selectedDetailData.metadata?.valuation_date || 'Latest update'}</small>
          </span>
          <span>₹{formatINR(selectedDetailData.total_value)}</span>
          <span>₹{formatINR(selectedDetailData.monthly_contribution || latestEntry?.monthly_contribution || 0)}</span>
          <span>{formatPercent(selectedDetailData.interest_rate || latestEntry?.interest_rate)}</span>
          <span>
            {selectedDetailData.projected_value !== null
              ? `₹${formatINR(selectedDetailData.projected_value)}`
              : 'N/A'}
            <Button
              variant="link"
              className="text-primary p-0 m-0 ms-3 me-2"
              style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
              onClick={() => handleOpenModal(assetTypes.find(a => a.key === 'ppf')!)}
            >
              Edit
            </Button>
            <Button
              variant="link"
              className="text-danger p-0 m-0 ms-2"
              style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
              onClick={() => handleDeletePPF()}
            >
              Delete
            </Button>
          </span>
        </div>
      );
    }

    if (selectedDetailKey === 'epf' && selectedDetailData.has_asset) {
      const members = Array.isArray(selectedDetailData.metadata?.members) ? selectedDetailData.metadata.members : [];
      if (members.length > 0) {
        return members.map((member: any) => (
          <div className="asset-scheme-table__row" key={member.id}>
            <span>
              <strong>{member.member_name || 'EPF member'}</strong>
              <small>{member.uan || member.member_id || 'Family member'}</small>
            </span>
            <span>₹{formatINR(member.current_balance)}</span>
            <span>₹{formatINR(member.employee_share || 0)}</span>
            <span>
              ₹{formatINR(member.employer_share || 0)} / ₹{formatINR(member.pension_share || 0)}
            </span>
            <span>
              {formatPercent(member.interest_rate)}
              <Button
                variant="link"
                className="text-primary p-0 m-0 ms-3 me-2"
                style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
                onClick={() => handleEditEPFMember(member)}
              >
                Edit
              </Button>
              <Button
                variant="link"
                className="text-danger p-0 m-0 ms-2"
                style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
                onClick={() => handleDeleteEPFMember(member.id)}
              >
                Delete
              </Button>
            </span>
          </div>
        ));
      }
    }

    if (selectedDetailKey === 'epf' && selectedDetailData.components.length > 0) {
      return selectedDetailData.components.map((component, idx) => (
        <div className="asset-scheme-table__row" key={component.name}>
          <span>
            <strong>{component.name}</strong>
            <small>{selectedDetailData.metadata?.member_id || selectedDetailData.metadata?.uan || 'EPF account'}</small>
          </span>
          <span>₹{formatINR(component.current_value)}</span>
          <span>₹{formatINR(component.contribution)}</span>
          <span>₹{formatINR(component.interest)}</span>
          <span>
            {component.status}
            {idx === 0 && (
              <>
                <Button
                  variant="link"
                  className="text-primary p-0 m-0 ms-3 me-2"
                  style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
                  onClick={() => handleOpenModal(assetTypes.find(a => a.key === 'epf')!)}
                >
                  Edit
                </Button>
                <Button
                  variant="link"
                  className="text-danger p-0 m-0 ms-2"
                  style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
                  onClick={() => handleDeleteEPF()}
                >
                  Delete
                </Button>
              </>
            )}
          </span>
        </div>
      ));
    }

    if (selectedDetailKey === 'gold' && selectedDetailData.has_asset) {
      const grams = Number(selectedDetailData.metadata?.grams || selectedDetailData.metadata?.quantity || 0);
      const buyPrice = Number(selectedDetailData.metadata?.buy_price_per_gram || selectedDetailData.metadata?.average_price_per_gram || 0);
      const currentPrice = Number(selectedDetailData.metadata?.current_price_per_gram || buyPrice);
      const holdingType = selectedDetailData.metadata?.holding_type === 'physical'
        ? 'Physical / in-hand gold'
        : 'Digital gold';
      return (
        <div className="asset-scheme-table__row">
          <span>
            <strong>{holdingType}</strong>
            <small>{selectedDetailData.metadata?.transaction_date || 'Latest update'}</small>
          </span>
          <span>₹{formatINR(selectedDetailData.total_value)}</span>
          <span>{grams.toLocaleString('en-IN', { maximumFractionDigits: 3 })} g</span>
          <span>
            ₹{formatINR(buyPrice)} / ₹{formatINR(currentPrice)}
          </span>
          <span>
            <span className={selectedDetailData.gain_loss >= 0 ? 'text-success' : 'text-danger'}>
              ₹{formatINR(selectedDetailData.gain_loss)}
            </span>
            <Button
              variant="link"
              className="text-primary p-0 m-0 ms-3 me-2"
              style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
              onClick={() => handleOpenModal(assetTypes.find(a => a.key === 'gold')!)}
            >
              Edit
            </Button>
            <Button
              variant="link"
              className="text-danger p-0 m-0 ms-2"
              style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
              onClick={() => handleDeleteGold()}
            >
              Delete
            </Button>
          </span>
        </div>
      );
    }


    if (selectedDetailKey === 'lic' && selectedDetailData.has_asset) {
      const policies = Array.isArray(selectedDetailData.metadata?.policies) ? selectedDetailData.metadata.policies : [];
      return policies.map((policy: any) => (
        <div className="asset-scheme-table__row" key={policy.id}>
          <span>
            <strong>{policy.policy_name}</strong>
            <small>{policy.policy_number || 'No Policy Number'}</small>
          </span>
          <span>₹{formatINR(policy.current_value)}</span>
          <span>₹{formatINR(policy.premium_amount)} ({policy.premium_frequency})</span>
          <span>
            ₹{formatINR(policy.sum_assured)}
            {policy.accrued_bonus > 0 && (
              <small className="text-success d-block" style={{ fontSize: '0.75rem' }}>
                Bonus: ₹{formatINR(policy.accrued_bonus)}
              </small>
            )}
          </span>
          <span>
            {policy.next_due_date ? `Due: ${policy.next_due_date}` : (policy.maturity_date ? `Matures: ${policy.maturity_date}` : 'N/A')}
            <Button
              variant="link"
              className="text-primary p-0 m-0 ms-2 me-2"
              style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
              onClick={() => handleEditPolicy(policy)}
            >
              Edit
            </Button>
            <Button
              variant="link"
              className="text-danger p-0 m-0 ms-2"
              style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
              onClick={() => handleDeletePolicy(policy.id)}
            >
              Delete
            </Button>
          </span>
        </div>
      ));
    }

    if (selectedDetailKey === 'real_estate' && selectedDetailData.has_asset) {
      const properties = Array.isArray(selectedDetailData.metadata?.properties) ? selectedDetailData.metadata.properties : [];
      return properties.map((property: any) => (
        <div className="asset-scheme-table__row" key={property.id}>
          <span>
            <strong>{property.property_name}</strong>
            <small>{property.location || 'No Location'}</small>
          </span>
          <span>₹{formatINR(property.current_value - property.loan_outstanding)}</span>
          <span>₹{formatINR(property.property_cost)}</span>
          <span>₹{formatINR(property.loan_outstanding)}</span>
          <span>
            {property.rental_income > 0 ? `₹${formatINR(property.rental_income)}/mo` : '—'}
            <Button
              variant="link"
              className="text-primary p-0 m-0 ms-2 me-2"
              style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
              onClick={() => handleEditProperty(property)}
            >
              Edit
            </Button>
            <Button
              variant="link"
              className="text-danger p-0 m-0 ms-2"
              style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
              onClick={() => handleDeleteProperty(property.id)}
            >
              Delete
            </Button>
          </span>
        </div>
      ));
    }

    if (selectedDetailKey === 'fixed_deposit' && selectedDetailData.has_asset) {
      const fds = Array.isArray(selectedDetailData.metadata?.fds) ? selectedDetailData.metadata.fds : [];
      return fds.map((fd: any) => (
        <div className="asset-scheme-table__row" key={fd.id}>
          <span>
            <strong>{fd.fd_name}</strong>
            <small>{fd.bank_name || 'No Bank'}</small>
          </span>
          <span>₹{formatINR(fd.current_value)}</span>
          <span>₹{formatINR(fd.principal)}</span>
          <span>{fd.interest_rate ? `${fd.interest_rate}% p.a.` : 'N/A'}</span>
          <span>
            {fd.maturity_date || 'N/A'}
            <Button
              variant="link"
              className="text-primary p-0 m-0 ms-2 me-2"
              style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
              onClick={() => handleEditFD(fd)}
            >
              Edit
            </Button>
            <Button
              variant="link"
              className="text-danger p-0 m-0 ms-2"
              style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
              onClick={() => handleDeleteFD(fd.id)}
            >
              Delete
            </Button>
          </span>
        </div>
      ));
    }

    if (selectedDetailKey === 'bank_account' && selectedDetailData.has_asset) {
      const accounts = Array.isArray(selectedDetailData.metadata?.accounts) ? selectedDetailData.metadata.accounts : [];
      return accounts.map((account: any) => (
        <div className="asset-scheme-table__row" key={account.id}>
          <span>
            <strong>{account.account_name}</strong>
            <small>{account.account_reference || 'No reference'}</small>
          </span>
          <span>₹{formatINR(account.balance)}</span>
          <span>{account.bank_name || 'No Bank'}</span>
          <span>{account.account_type || 'Savings'}</span>
          <span>
            {account.last_updated || 'N/A'}
            <Button
              variant="link"
              className="text-primary p-0 m-0 ms-2 me-2"
              style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
              onClick={() => handleEditBankAccount(account)}
            >
              Edit
            </Button>
            <Button
              variant="link"
              className="text-danger p-0 m-0 ms-2"
              style={{ fontSize: '0.72rem', textDecoration: 'none', verticalAlign: 'middle' }}
              onClick={() => handleDeleteBankAccount(account.id)}
            >
              Delete
            </Button>
          </span>
        </div>
      ));
    }

    return selectedDetailConfig.rows.map((row, rowIndex) => (
      <div className="asset-scheme-table__row asset-scheme-table__row--placeholder" key={`${selectedDetailKey}-${rowIndex}`}>
        {row.map((cell, cellIndex) => (
          <span key={`${cell}-${cellIndex}`}>
            {cellIndex === 0 ? <strong>{cell}</strong> : cell}
          </span>
        ))}
      </div>
    ));
  };


  return (
    <div className="assets-page">
      {/* Hero section — mirrors dashboard-hero */}
      <section className="assets-hero">
        <span className="assets-hero__kicker">Asset Dashboard</span>
        <h1 className="assets-hero__title">Assets</h1>
        <p className="assets-hero__subtitle">
          Track NPS, PPF, EPF, and Gold from one place. Import statements or add
          balances to see your retirement and savings portfolio come alive.
        </p>
        <div className="assets-hero__actions">
          <Button variant="outline-dark" href="/analysis">Open Wealth Analysis</Button>
        </div>
      </section>

      {loadError && (
        <Alert variant="warning" className="d-flex justify-content-between align-items-center gap-3">
          <span>{loadError}</span>
          <Button variant="outline-dark" size="sm" onClick={fetchAssets}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Net Worth Top Card */}
      <Card className="mb-4 assets-net-worth-card">
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col xs={12} md={4} className="assets-net-worth-card__total-col border-end">
              <span className="assets-net-worth-card__label">Total Net Worth</span>
              <h2 className="assets-net-worth-card__value">
                {assetsLoading || portfolioLoading ? (
                  <span className="d-inline-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    Calculating
                  </span>
                ) : (
                  <>₹{formatINR(portfolioValue + Object.values(assetsData).reduce((sum, asset) => sum + (asset?.total_value ?? 0), 0))}</>
                )}
              </h2>
            </Col>
            <Col xs={12} md={8} className="mt-3 mt-md-0">
              <div className="assets-net-worth-card__breakdown">
                <div className="assets-net-worth-card__breakdown-item">
                  <span className="assets-net-worth-card__item-label">Portfolio</span>
                  <strong className="assets-net-worth-card__item-val">
                    {portfolioLoading ? 'Loading…' : `₹${formatINR(portfolioValue)}`}
                  </strong>
                </div>
                {assetTypes.map((asset) => {
                  const val = assetsData[asset.key]?.total_value ?? 0;
                  return (
                    <div className="assets-net-worth-card__breakdown-item" key={asset.key}>
                      <span className="assets-net-worth-card__item-label">{asset.name}</span>
                      <strong className="assets-net-worth-card__item-val">
                        {assetsLoading ? 'Loading…' : (val > 0 ? `₹${formatINR(val)}` : '—')}
                      </strong>
                    </div>
                  );
                })}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-4">
        {assetTypes.map((asset) => {
          const data = assetsData[asset.key];
          const isActive = data.has_asset;
          const isGain = data.gain_loss >= 0;
          const metadataCollectionCount = Math.max(
            0,
            ...Object.values(data.metadata || {})
              .filter(Array.isArray)
              .map((value) => value.length),
          );
          const trackedItemCount = Math.max(
            data.entries?.length || 0,
            data.components?.length || 0,
            metadataCollectionCount,
          );
          const supplementaryMetric = data.xirr !== null && data.xirr !== undefined
            ? {
                label: 'XIRR',
                value: `${data.xirr.toFixed(2)}%`,
                tone: data.xirr >= 0 ? 'asset-metric-value--gain' : 'asset-metric-value--loss',
              }
            : data.interest_rate !== null && data.interest_rate !== undefined
              ? {
                  label: 'Rate',
                  value: `${data.interest_rate.toFixed(2)}%`,
                  tone: 'asset-metric-value--gain',
                }
              : data.monthly_contribution !== null && data.monthly_contribution !== undefined
                ? {
                    label: 'Monthly Add',
                    value: `₹${formatINR(data.monthly_contribution)}`,
                    tone: '',
                  }
                : trackedItemCount > 0
                  ? {
                      label: trackedItemLabels[asset.key],
                      value: String(trackedItemCount),
                      tone: '',
                    }
                  : {
                      label: asset.key === 'nps' ? 'XIRR' : 'Details',
                      value: 'N/A',
                      tone: '',
                    };

          return (
            <Col key={asset.key} xs={12} md={6} xl={4} xxl={3}>
              <Card
                className={`h-100 asset-card ${selectedDetailKey === asset.key ? 'asset-card--selected' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedDetailKey(asset.key)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedDetailKey(asset.key);
                  }
                }}
              >
                {/* Accent strip using theme gradients */}
                <div
                  className="asset-card__accent"
                  style={{ ['--accent-gradient' as string]: asset.accentGradient, background: asset.accentGradient }}
                />

                <Card.Body className="d-flex flex-column p-4">
                  <div className="d-flex align-items-start justify-content-between mb-3">
                    <div className="asset-card__icon"><AppIcon name={asset.icon} /></div>
                    <span className={`asset-badge ${isActive ? 'asset-badge--active' : 'asset-badge--empty'}`}>
                      {assetsLoading ? 'Loading' : (isActive ? 'Active' : 'Not linked')}
                    </span>
                  </div>

                  <h5 className="fw-bold mb-1" style={{ color: 'var(--pd-text-dark)' }}>{asset.name}</h5>
                  <p className="asset-description mb-4 flex-grow-1">{asset.description}</p>

                  {/* Metrics panel — matches dashboard-overview-card */}
                  <div className="asset-metrics mb-4">
                    <Row className="g-3 text-center">
                      <Col xs={6} className="border-end border-bottom pb-2">
                        <span className="asset-metric-label">{asset.key === 'nps' ? 'Total Value' : 'Current'}</span>
                        <span className="asset-metric-value">
                          {assetsLoading ? '—' : `₹${formatINR(data.total_value)}`}
                        </span>
                      </Col>
                      <Col xs={6} className="border-bottom pb-2">
                        <span className="asset-metric-label">{asset.key === 'nps' ? 'Investment' : 'Invested'}</span>
                        <span className="asset-metric-value">
                          {assetsLoading ? '—' : `₹${formatINR(data.investment)}`}
                        </span>
                      </Col>
                      <Col xs={6} className="border-end pt-2">
                        <span className="asset-metric-label">Gain/Loss</span>
                        <span className={`asset-metric-value ${isGain ? 'asset-metric-value--gain' : 'asset-metric-value--loss'}`}>
                          {assetsLoading ? '—' : `₹${formatINR(data.gain_loss)}`}
                        </span>
                      </Col>
                      <Col xs={3} className="border-end pt-2">
                        <span className="asset-metric-label">Return (Abs)</span>
                        <span className={`asset-metric-value ${isGain ? 'asset-metric-value--gain' : 'asset-metric-value--loss'}`}>
                          {assetsLoading ? '—' : `${data.return_percent.toFixed(2)}%`}
                        </span>
                      </Col>
                      <Col xs={3} className="pt-2">
                        <span className="asset-metric-label">{supplementaryMetric.label}</span>
                        <span className={`asset-metric-value ${supplementaryMetric.tone}`}>
                          {assetsLoading ? '—' : supplementaryMetric.value}
                        </span>
                      </Col>
                    </Row>
                  </div>

                  {!assetsLoading && !isActive && (
                    <div className="asset-hint mb-3">
                      {asset.key === 'nps' && 'Import your NPS statement to calculate current value and returns.'}
                      {asset.key === 'ppf' && 'Add your PPF balance to project financial-year interest.'}
                      {asset.key === 'epf' && 'Import your EPFO passbook or add balances manually.'}
                      {asset.key === 'gold' && 'Add digital or in-hand gold to track market value.'}
                      {asset.key === 'lic' && 'Add your life insurance policies to track maturity values and premiums.'}
                      {asset.key === 'real_estate' && 'Add your properties to track property values, loans, and rental incomes.'}
                      {asset.key === 'fixed_deposit' && 'Add your bank FDs to track principal, interest rate, and maturity values.'}
                      {asset.key === 'bank_account' && 'Add liquid bank balances to include cash in your net worth.'}
                    </div>
                  )}

                  <Button
                    variant="outline-primary"
                    className="w-100 mt-auto py-2 asset-cta"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenModal(asset);
                    }}
                  >
                    {asset.ctaLabel}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      <section className="asset-detail-panel mt-4">
        <div className="asset-detail-panel__header">
          <div>
            <span className="asset-detail-panel__eyebrow">{selectedDetailConfig.eyebrow}</span>
            <h2 className="asset-detail-panel__title">{selectedDetailConfig.title}</h2>
            <p className="asset-detail-panel__copy">{selectedDetailConfig.emptyText}</p>
          </div>
          <div className="asset-detail-panel__summary">
            <span>{selectedDetailConfig.summaryLabel}</span>
            <strong>
              {selectedDetailKey === 'lic'
                ? (selectedSummaryValue !== null && selectedSummaryValue !== undefined
                  ? `${selectedSummaryValue} ${selectedSummaryValue === 1 ? 'Policy' : 'Policies'}`
                  : '0 Policies')
                : selectedDetailKey === 'nps'
                  ? (selectedSummaryValue !== null && selectedSummaryValue !== undefined
                    ? `${selectedSummaryValue} ${selectedSummaryValue === 1 ? 'Account' : 'Accounts'}`
                    : '0 Accounts')
                : selectedDetailKey === 'ppf'
                  ? (selectedSummaryValue !== null && selectedSummaryValue !== undefined
                    ? `${selectedSummaryValue} ${selectedSummaryValue === 1 ? 'Account' : 'Accounts'}`
                    : '0 Accounts')
                : selectedDetailKey === 'epf'
                  ? (selectedSummaryValue !== null && selectedSummaryValue !== undefined
                    ? `${selectedSummaryValue} ${selectedSummaryValue === 1 ? 'Member' : 'Members'}`
                    : '0 Members')
                : selectedDetailKey === 'fixed_deposit'
                  ? (selectedSummaryValue !== null && selectedSummaryValue !== undefined
                    ? `${selectedSummaryValue} ${selectedSummaryValue === 1 ? 'FD' : 'FDs'}`
                    : '0 FDs')
                  : selectedDetailKey === 'bank_account'
                    ? (selectedSummaryValue !== null && selectedSummaryValue !== undefined
                      ? `${selectedSummaryValue} ${selectedSummaryValue === 1 ? 'Account' : 'Accounts'}`
                      : '0 Accounts')
                  : selectedDetailKey === 'real_estate'
                    ? (selectedSummaryValue !== null && selectedSummaryValue !== undefined
                      ? `₹${formatINR(selectedSummaryValue)}`
                      : '₹0')
                    : (selectedSummaryValue !== null && selectedSummaryValue !== undefined
                      ? `${Number(selectedSummaryValue).toFixed(2)}%`
                      : 'N/A')}
            </strong>
          </div>
        </div>

        <div className="asset-scheme-table">
          <div className="asset-scheme-table__head">
            {selectedDetailConfig.columns.map((column) => (
              <span key={column}>{column}</span>
            ))}
          </div>
          {renderDetailRows()}
        </div>
      </section>

      {/* Reusable Import / Add Asset Modal */}
      <AssetActionModal
        show={showModal}
        assetType={selectedAsset?.key || null}
        mode={selectedAsset?.key === 'nps' || selectedAsset?.key === 'epf' ? 'import' : 'manual'}
        onHide={handleCloseModal}
        onSubmit={handleModalSubmit}
        policyToEdit={policyToEdit}
        propertyToEdit={propertyToEdit}
        fdToEdit={fdToEdit}
        bankAccountToEdit={bankAccountToEdit}
        ppfAccountToEdit={ppfAccountToEdit}
        epfMemberToEdit={epfMemberToEdit}
        initialData={selectedAsset ? assetsData[selectedAsset.key] : null}
      />

    </div>
  );
};

export default Assets;
